import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Reviews from "../components/Reviews";
import { StarRating } from "../components/StarRating";
import VerifiedBadge from "../components/VerifiedBadge";
import InfoTooltip from "../components/InfoTooltip";
import { useCart } from "../context/CartContext";
import { useSeo } from "../lib/seo";
import ServiceCard from "../components/ServiceCard";
import SafeImage from "../components/SafeImage";
import ReportBusinessModal from "../components/ReportBusinessModal";
import BookingQuestionsForm, { validateAnswers, answersPayload } from "../components/BookingQuestionsForm";
import { Lock } from "lucide-react";
import { useCheckoutQuote, apptTzId, money, consentSignature } from "../lib/checkoutQuote";

export default function ServiceDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ack, setAck] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discount }
  const [promoBusy, setPromoBusy] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [related, setRelated] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [answers, setAnswers] = useState({});
  // Iteration 83 — request-first businesses. `requestId` arrives from the
  // customer's accepted request ("Finish booking"); without it, this page
  // sends a request instead of starting payment.
  const requestId = new URLSearchParams(window.location.search).get("request_id");
  const { user, authHeader } = useAuth();
  const { add: addToCart } = useCart();
  const nav = useNavigate();
  // Prefilled date/time handed over from the storefront Book slide-over.
  const prefillTimeRef = useRef(null);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const qd = sp.get("date"); const qt = sp.get("time");
    if (qt) prefillTimeRef.current = qt;
    if (qd) setDate(qd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSeo({
    title: s && s.title
      ? `${s.title} — Book on BookFreely`
      : "Book a service — BookFreely",
    description: s && s.description
      ? String(s.description).slice(0, 155)
      : "Book this service securely on BookFreely with transparent deposits and automated reminders.",
    path: `/services/${id}`,
  });

  useEffect(() => {
    axios.get(`${API}/services/${id}`).then((r) => setS(r.data)).catch(() => setS(false));
    axios.get(`${API}/services/${id}/related`, { params: { limit: 4 } })
      .then((r) => setRelated(r.data || [])).catch(() => setRelated([]));
    axios.get(`${API}/platform/config`).then((r) => {
      setCfg(r.data || null);
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!date || !s) { setSlots([]); setTime(""); return; }
    axios.get(`${API}/services/${id}/availability`, { params: { date } })
      .then((r) => {
        const open = r.data.slots || [];
        setSlots(open);
        const pfRaw = prefillTimeRef.current;
        if (pfRaw && open.length) {
          // A "+" in the ?time= query can arrive decoded as a space — normalize it back.
          const pf = pfRaw.includes(" ") ? pfRaw.replace(/ /g, "+") : pfRaw;
          const pfT = new Date(pf).getTime();
          const match = open.find((iso) => iso === pf || new Date(iso).getTime() === pfT);
          setTime(match || "");
          prefillTimeRef.current = null; // consumed once real slots arrived
        } else if (!pfRaw) {
          setTime("");
        }
        // pf set but no slots yet → keep the ref so the next fetch can match it
      })
      .catch(() => setSlots([]));
  }, [date, id, s]);

  // Service structured data for rich results.
  useEffect(() => {
    if (!s || s === false) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: s.title,
      description: s.description || undefined,
      image: s.image_url || undefined,
      serviceType: s.category || undefined,
      provider: { "@type": "LocalBusiness", name: s.business_name || undefined },
      offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        price: Number(s.price || 0).toFixed(2),
        availability: "https://schema.org/InStock",
      },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-bf-ld", "service");
    el.textContent = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => {
      const ex = document.head.querySelector('script[data-bf-ld="service"]');
      if (ex) ex.remove();
    };
  }, [s]);

  // Jun 2026 money-accuracy repair — every amount, the timing and the exact
  // authorization sentence come from the backend, which uses the SAME helper
  // that builds the Stripe Checkout Session. The page never computes them.
  const apptTz = apptTzId();
  const { quote } = useCheckoutQuote({
    mode: "booking",
    items: [{ service_id: id, scheduled_at: time || null, appointment_tz: apptTz }],
    promoCode: appliedPromo ? appliedPromo.code : null,
    authHeader,
    enabled: !!s && !(s.business_booking_method === "request" && !requestId),
  });
  const q = (quote && quote.items && quote.items[0]) || null;
  const qSig = consentSignature(q);
  // Any change to the amounts, the timing or the appointment clears the tick box.
  useEffect(() => { setAck(false); }, [qSig]);

  if (s === null) return <div className="App"><Header /><div className="max-w-3xl mx-auto p-12" style={{ color: "var(--gf-text-muted)" }}>Loading...</div></div>;
  if (s === false) return (
    <div className="App" data-testid="service-not-found">
      <Header />
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <div
          className="mx-auto flex items-center justify-center w-20 h-20 rounded-full border"
          style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)" }}
          aria-hidden="true"
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </div>
        <div className="uppercase text-xs tracking-[0.25em] mt-6" style={{ color: "var(--gf-text-muted)" }}>
          Service unavailable
        </div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2" style={{ color: "var(--gf-text)" }}>
          We couldn&rsquo;t find that service
        </h1>
        <p className="mt-3 text-sm" style={{ color: "var(--gf-text-muted)" }}>
          It may have been removed by the business, be temporarily paused, or the link
          may be broken. Try browsing similar services below.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link
            to="/services"
            data-testid="service-not-found-browse-btn"
            className="px-5 py-2 rounded-full font-medium"
            style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
          >
            Browse all services
          </Link>
          <Link
            to="/"
            data-testid="service-not-found-home-btn"
            className="px-5 py-2 rounded-full font-medium border"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          >
            Back to home
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );

  // Request-first businesses: no payment UI, no consent, no card.
  const requestMode = s?.business_booking_method === "request" && !requestId;

  const sendRequest = async () => {
    if (!user) { nav("/auth?mode=login"); return; }
    if (!time) { toast.error("Pick a date and time"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/booking-requests`,
        { service_id: id, requested_at: time, note: notes || null },
        { headers: authHeader() });
      toast.success("Request sent — the business will reply shortly");
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that request");
    } finally {
      setSubmitting(false);
    }
  };

  const book = async () => {
    if (!user) { nav("/login?next=/services/" + id); return; }
    if (user.role !== "customer") { toast.error("Only customers can book services"); return; }
    if (!date || !time) { toast.error("Pick a date and time"); return; }
    const bookingQuestions = s?.booking_questions || [];
    const qErr = validateAnswers(bookingQuestions, answers);
    if (qErr) { toast.error(qErr); return; }
    setSubmitting(true);
    try {
      // `time` is already a full ISO string from the slot picker (see setTime(iso) below).
      const scheduled_at = time;
      const r = await axios.post(
        `${API}/bookings`,
        {
          service_id: id,
          scheduled_at,
          notes,
          origin_url: window.location.origin,
          promo_code: appliedPromo ? appliedPromo.code : null,
          payment_consent: true,
          consent_version: q?.consent_version || cfg?.payment_consent_version || null,
          // Straight from the backend quote — never recomputed here.
          authorized_today_cents: q ? q.authorized_today_cents : undefined,
          authorized_balance_cents: q ? q.authorized_balance_cents : undefined,
          appointment_tz: apptTz,
          answers: bookingQuestions.length ? answersPayload(bookingQuestions, answers) : undefined,
          request_id: requestId || undefined,
        },
        { headers: authHeader() }
      );
      if (r.data.url) {
        // Deposit required — Stripe Checkout redirect
        window.location.href = r.data.url;
      } else {
        // No deposit — booking request created instantly
        toast.success("Booking request sent! The business will confirm shortly.");
        setTimeout(() => nav("/account"), 1500);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start booking");
    } finally {
      setSubmitting(false);
    }
  };

  const applyPromo = async () => {
    if (!user) { toast.error("Please sign in to use a promo code"); return; }
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    if (!requiresDeposit) { toast.error("Promo codes only apply when a deposit is due today"); return; }
    setPromoBusy(true);
    try {
      const r = await axios.post(
        `${API}/promo-codes/validate`,
        { code, subtotal: depositAmount, category: s?.category },
        { headers: authHeader() }
      );
      setAppliedPromo({ code: r.data.code, discount: Number(r.data.discount || 0) });
      toast.success(`Promo "${r.data.code}" applied — saved $${Number(r.data.discount || 0).toFixed(2)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply promo code");
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = () => { setAppliedPromo(null); setPromoInput(""); };

  // Derived deposit only — used for the promo form, the refund tag and the
  // add-to-cart snapshot. Every AMOUNT the customer sees now comes from the
  // backend quote (`q`), never from these locals.
  const requiresDeposit = s ? (s.requires_deposit !== false && Number(s.downpayment_amount || 0) > 0) : false;
  const depositAmount = !s ? 0
    : s.deposit_type === "percent"
      ? +(s.price * (s.downpayment_amount / 100)).toFixed(2)
      : +Number(s.downpayment_amount || 0).toFixed(2);
  const promoDiscount = appliedPromo ? Math.min(appliedPromo.discount, depositAmount) : 0;

  const inputCls = "mt-1 w-full bg-transparent border rounded-xl px-3.5 py-2.5 outline-none focus:border-[var(--gf-accent)]";
  const inputStyle = { borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" };

  return (
    <div className="App">
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <div className="rounded-2xl overflow-hidden border aspect-[16/10]" style={{ borderColor: "var(--gf-border)", background: "var(--gf-bg-soft)" }}>
            {s.image_url ? (
              <SafeImage src={s.image_url} alt={s.title} testId="service-hero-image" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-display text-4xl opacity-30" style={{ color: "var(--gf-text)" }}>
                Book<span style={{ color: "var(--gf-accent)" }}>Freely</span>
              </div>
            )}
          </div>
          <div className="mt-6 uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>{s.category}</div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <h1 data-testid="service-title" className="font-display text-4xl sm:text-5xl" style={{ color: "var(--gf-text)" }}>{s.title}</h1>
            {(s.business_trust_badges || []).includes("verified") && <VerifiedBadge size="sm" />}
          </div>
          <div className="mt-2"><StarRating value={s.avg_rating || 0} count={s.rating_count || 0} /></div>
          <div className="mt-2" style={{ color: "var(--gf-text-muted)" }}>
            by <Link to={`/business/${s.business_id}`} className="hover:underline" style={{ color: "var(--gf-text)" }} data-testid="service-business-link">{s.business_name}</Link> · {s.duration_minutes} min{s.location ? ` · ${s.location}` : ""}
          </div>

          {(s.business_phone || s.business_email) ? (
            <div data-testid="business-contact-card" className="mt-4 rounded-xl border p-4 text-sm"
                 style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Contact the business</div>
              <div className="mt-1 space-y-1" style={{ color: "var(--gf-text)" }}>
                {s.business_phone && <div><a href={`tel:${s.business_phone}`} className="hover:underline" data-testid="business-phone">📞 {s.business_phone}</a></div>}
                {s.business_email && <div><a href={`mailto:${s.business_email}`} className="hover:underline" data-testid="business-email">✉ {s.business_email}</a></div>}
                {s.business_website && <div><a href={/^https?:\/\//i.test(s.business_website) ? s.business_website : "https://" + s.business_website} target="_blank" rel="noreferrer" className="hover:underline" data-testid="business-website">↗ {s.business_website.replace(/^https?:\/\//, "")}</a></div>}
              </div>
              <div className="mt-2 text-xs" style={{ color: "var(--gf-text-muted)" }}>
                For anything about your appointment — rescheduling, questions, or a refund request — contact {s.business_name} directly.
              </div>
            </div>
          ) : (
            <div data-testid="contact-locked-note" className="mt-4 rounded-xl border p-4 text-sm"
                 style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Contact the business</div>
              <p className="mt-1" style={{ color: "var(--gf-text)" }}>
                Message {s.business_name} through BookFreely. Their phone number and email are shared once a booking with them is confirmed.
              </p>
              {s.business_website && (
                <div className="mt-1"><a href={/^https?:\/\//i.test(s.business_website) ? s.business_website : "https://" + s.business_website} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: "var(--gf-text)" }} data-testid="business-website">↗ {s.business_website.replace(/^https?:\/\//, "")}</a></div>
              )}
            </div>
          )}

          <p className="mt-6 leading-relaxed whitespace-pre-line" style={{ color: "var(--gf-text-soft)" }}>{s.description}</p>

          <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="service-actions">
            <Link
              to={`/business/${s.business_id}`}
              data-testid="view-business-profile-btn"
              className="text-sm rounded-full border px-4 py-2"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              View Business Profile
            </Link>
            <Link
              to={`/business/${s.business_id}`}
              data-testid="message-business-btn"
              className="text-sm rounded-full px-4 py-2 font-medium"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              Message business
            </Link>
            <button
              type="button"
              data-testid="report-service-btn"
              onClick={() => {
                if (!user) { nav(`/login?next=/services/${id}`); return; }
                if (user.role !== "customer") { toast.error("Only customers can file a report"); return; }
                setReportOpen(true);
              }}
              className="text-sm rounded-full border px-4 py-2 transition-colors hover:bg-[var(--gf-surface-2)] cursor-pointer"
              style={{ borderColor: user ? "var(--gf-border-strong)" : "var(--gf-accent)",
                       color: user ? "var(--gf-text-soft)" : "var(--gf-accent)",
                       textDecoration: user ? "none" : "underline" }}
            >
              {user ? "Report" : "Sign in to report"}
            </button>
          </div>

          <Reviews service={s} />

          {related.length > 0 && (
            <div className="mt-12" data-testid="related-services">
              <h2 className="font-display text-2xl" style={{ color: "var(--gf-text)" }}>
                More services from {s.business_name}
              </h2>
              <div className="mt-5 grid sm:grid-cols-2 gap-6">
                {related.slice(0, 4).map((r) => <ServiceCard key={r.id} s={r} />)}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="sticky top-24 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Service price</div>
                <div className="font-display text-3xl" style={{ color: "var(--gf-text)" }}>${Number(s.price || 0).toFixed(2)}</div>
              </div>
              {!requestMode && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.2em]" data-testid="today-label" style={{ color: "var(--gf-text-muted)" }}>
                  {q ? q.today_label : "Due today"}
                </div>
                <div className="font-display text-3xl" style={{ color: (q && q.service_amount_today_cents > 0) ? "var(--gf-accent)" : "var(--gf-text)" }} data-testid="sidebar-deposit-amount">
                  {q ? money(q.service_amount_today_cents) : "…"}
                </div>
                {q && (
                  <p className="mt-2 text-xs" data-testid="future-charge-explainer" style={{ color: "var(--gf-text-soft)" }}>
                    {q.timing_note}
                  </p>
                )}
              </div>
              )}
              {requestMode && (
                <div className="text-right text-xs max-w-[55%]" data-testid="request-mode-no-charge" style={{ color: "var(--gf-text-soft)" }}>
                  Nothing is charged and no card is saved until {s.business_name} accepts your requested time.
                </div>
              )}
            </div>
            <div data-testid="refund-tag" className="mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs"
                 style={{
                   borderColor: !requiresDeposit ? "var(--gf-border-strong)"
                     : s.downpayment_refundable ? "var(--gf-surface-2)" : "var(--gf-border)",
                   color: !requiresDeposit ? "var(--gf-text-muted)"
                     : s.downpayment_refundable ? "var(--gf-accent)" : "var(--gf-text-muted)"
                 }}>
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              {!requiresDeposit ? "No deposit required" : (s.downpayment_refundable ? "Refundable deposit" : "Non-refundable deposit")}
            </div>

            {requestMode ? (
            <div data-testid="request-mode-payment-note" className="mt-5 rounded-xl border px-4 py-3 text-xs leading-relaxed"
                 style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-soft)" }}>
              <div className="flex items-center justify-between text-sm">
                <span style={{ color: "var(--gf-text-soft)" }}>Service price</span>
                <span style={{ color: "var(--gf-text)" }} data-testid="breakdown-service-price">${s.price.toFixed(2)}</span>
              </div>
              <p className="mt-2">
                No payment is set up yet. Once {s.business_name} accepts your time you&rsquo;ll get a Finish Booking
                link with the exact amounts before anything is charged.
              </p>
            </div>
            ) : (
            <div data-testid="charge-breakdown" className="mt-5 rounded-xl border divide-y" style={{ borderColor: "var(--gf-border)" }}>
              <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                <span style={{ color: "var(--gf-text-soft)" }}>Service price</span>
                <span style={{ color: "var(--gf-text)" }} data-testid="breakdown-service-price">${s.price.toFixed(2)}</span>
              </div>

              {!q ? (
                <div className="px-4 py-3 text-sm" data-testid="breakdown-loading" style={{ color: "var(--gf-text-muted)" }}>
                  Calculating amounts…
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span style={{ color: "var(--gf-text-soft)" }}>
                      {q.charge_full_today ? "Service amount due today" : q.service_amount_today_cents > 0 ? "Deposit due today" : "Service amount due today"}
                      {!q.charge_full_today && q.service_amount_today_cents > 0 && s.deposit_type === "percent" && (
                        <span className="text-xs ml-1" style={{ color: "var(--gf-text-muted)" }}>({s.downpayment_amount}% of service)</span>
                      )}
                    </span>
                    <span style={{ color: "var(--gf-text)" }} data-testid="breakdown-downpayment">
                      {money(q.service_amount_today_cents + q.discount_cents)}
                    </span>
                  </div>
                  {q.discount_cents > 0 && (
                    <div className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid="breakdown-promo-row">
                      <span style={{ color: "var(--gf-accent)" }}>Promo · {appliedPromo?.code}</span>
                      <span style={{ color: "var(--gf-accent)" }} data-testid="breakdown-promo-discount">−{money(q.discount_cents)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="flex items-center gap-1.5" style={{ color: "var(--gf-text-soft)" }}>
                      Booking &amp; processing fee (3.85%)
                      <InfoTooltip text="Single upfront fee that covers card processing for this booking. You will never be charged this fee twice." />
                    </span>
                    <span style={{ color: "var(--gf-text)" }} data-testid="breakdown-processing-fee">{money(q.processing_fee_cents)}</span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--gf-bg-soft)" }}>
                    <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--gf-text-muted)" }}>Total charged today</span>
                    <span className="font-display text-xl" style={{ color: "var(--gf-text)" }} data-testid="breakdown-total">{money(q.total_today_cents)}</span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid="breakdown-balance-row">
                    <span style={{ color: "var(--gf-text-muted)" }} data-testid="breakdown-balance-label">Remaining future charge</span>
                    <span style={{ color: "var(--gf-text)" }} data-testid="breakdown-balance">{money(q.remaining_balance_cents)}</span>
                  </div>
                  {q.pay_in_person_cents > 0 && (
                    <div className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid="breakdown-in-person-row">
                      <span style={{ color: "var(--gf-text-muted)" }}>Paid to the business at the appointment</span>
                      <span style={{ color: "var(--gf-text)" }}>{money(q.pay_in_person_cents)}</span>
                    </div>
                  )}
                  <div className="px-4 py-2.5 text-xs" data-testid="remaining-balance-timing" style={{ color: "var(--gf-text-muted)" }}>
                    {q.timing_note}
                  </div>
                </>
              )}
            </div>
            )}

            <div className="mt-6 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Date</label>
                <input data-testid="booking-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} style={inputStyle} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Time</label>
                {date ? (
                  slots.length === 0 ? (
                    <div className="mt-1 text-xs p-3 rounded-md border" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}>
                      No open slots on this date.
                    </div>
                  ) : (
                    <div data-testid="time-slots" className="mt-1 flex flex-wrap gap-2">
                      {slots.map((iso) => {
                        const t = new Date(iso);
                        const label = t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
                        const active = time === iso;
                        return (
                          <button key={iso} type="button" data-testid={`slot-${iso}`} onClick={() => setTime(iso)}
                            className="text-sm font-medium rounded-xl border px-3.5 py-2 transition-colors"
                            style={{
                              background: active ? "var(--gf-accent)" : "transparent",
                              color: active ? "var(--gf-on-accent)" : "var(--gf-text)",
                              borderColor: active ? "var(--gf-accent)" : "var(--gf-border-strong)",
                            }}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : (
                  <div className="mt-1 text-xs" style={{ color: "var(--gf-text-muted)" }}>Pick a date first</div>
                )}
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Notes (optional)</label>
                <textarea data-testid="booking-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className={inputCls} style={inputStyle} placeholder="Anything the business should know..." />
              </div>
              {requiresDeposit && !requestMode && (
                <div data-testid="promo-section">
                  <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Promo / discount code</label>
                  {appliedPromo ? (
                    <div className="mt-1 flex items-center justify-between rounded-md border px-3 py-2" style={{ borderColor: "var(--gf-accent)", background: "var(--gf-surface)" }}>
                      <span className="text-sm" style={{ color: "var(--gf-accent)" }} data-testid="promo-applied-pill">
                        {appliedPromo.code} · −${promoDiscount.toFixed(2)} off deposit
                      </span>
                      <button data-testid="promo-clear-btn" onClick={clearPromo} type="button" className="text-xs underline" style={{ color: "var(--gf-text-muted)" }}>
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="mt-1 flex gap-2">
                      <input
                        data-testid="promo-input"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        placeholder="ENTER CODE"
                        className={`${inputCls} mt-0 uppercase`}
                        style={inputStyle}
                      />
                      <button
                        data-testid="promo-apply-btn"
                        type="button"
                        onClick={applyPromo}
                        disabled={promoBusy || !promoInput.trim()}
                        className="rounded-md px-4 text-sm font-medium disabled:opacity-50"
                        style={{ background: "var(--gf-text)", color: "var(--gf-bg)" }}
                      >
                        {promoBusy ? "…" : "Apply"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {(s.booking_questions || []).length > 0 && (
              <div className="mt-5 rounded-md border p-4" data-testid="booking-questions-section"
                   style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface)" }}>
                <BookingQuestionsForm
                  questions={s.booking_questions}
                  answers={answers}
                  onChange={setAnswers}
                  authHeader={authHeader}
                  idPrefix="bq"
                />
              </div>
            )}

            {requestMode && (
              <div className="mt-5 rounded-md border p-3 text-xs leading-relaxed" data-testid="request-mode-note"
                   style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface-2)", color: "var(--gf-text-soft)" }}>
                <b style={{ color: "var(--gf-text)" }}>{s.business_name}</b> reviews times before confirming. Send your
                preferred time — nothing is charged and no card is saved. You&rsquo;ll pay only after the business accepts.
              </div>
            )}

            {!requestMode && (
            <div data-testid="booking-disclaimer" className="mt-5 rounded-md border p-3 text-xs leading-relaxed"
                 style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface-2)", color: "var(--gf-text-soft)" }}>
              Service provided by <strong style={{ color: "var(--gf-text)" }}>{s.business_name}</strong>, an independent business.
              BookFreely handles the booking and payment. For service issues, message the business first or report a
              problem to BookFreely. Refunds follow the displayed{" "}
              <Link to="/terms#cancellations" className="underline" data-testid="cancellation-policy-link">cancellation policy</Link>{" "}
              and <Link to="/terms" className="underline" data-testid="bookfreely-terms-link">BookFreely Terms</Link>.
              <label className="mt-3 flex items-start gap-2 cursor-pointer" style={{ color: "var(--gf-text)" }}>
                <input data-testid="booking-ack-checkbox" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" style={{ accentColor: "var(--gf-accent)" }} />
                <span data-testid="payment-authorization-text">
                  {q ? q.consent_wording : "Calculating the exact amounts you’re authorizing…"}
                </span>
              </label>
              {q && (
                <p className="mt-2 text-xs" data-testid="future-charge-explainer-detail" style={{ color: "var(--gf-text-soft)" }}>
                  {q.timing_note}
                </p>
              )}
            </div>
            )}

            {requestMode ? (
              <button data-testid="send-request-btn" onClick={sendRequest} disabled={submitting || !time}
                className="mt-5 w-full rounded-full font-medium py-3 transition-colors disabled:opacity-50"
                style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                {submitting ? "Sending request…" : "Request this time"}
              </button>
            ) : (
            <button data-testid="book-now-btn" onClick={book} disabled={submitting || !ack || !q}
              className="mt-5 w-full rounded-full font-medium py-3 transition-colors disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              {submitting
                ? ((q && q.total_today_cents > 0) ? "Redirecting to secure checkout…" : "Sending request…")
                : (q && q.total_today_cents > 0)
                  ? `Pay ${money(q.total_today_cents)} to book`
                  : (q && q.save_card) ? "Save card & request booking" : "Request booking"
              }
            </button>
            )}
            {!requestMode && (
            <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px]"
               data-testid="stripe-secure-note" style={{ color: "var(--gf-text-muted)" }}>
              <Lock size={11} strokeWidth={2} aria-hidden="true" />
              Secure checkout powered by Stripe
            </p>
            )}

            {q && q.total_today_cents > 0 && (
              <div data-testid="checkout-wallets-hint" className="mt-2 flex items-center justify-center gap-2 text-[11px]" style={{ color: "var(--gf-text-muted)" }}>
                <span>Pay with</span>
                <span className="rounded-md border px-2 py-0.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Card</span>
                <span className="rounded-md border px-2 py-0.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Apple Pay</span>
                <span className="rounded-md border px-2 py-0.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Google Pay</span>
              </div>
            )}
            {!requestMode && (
            <button
              data-testid="add-to-cart-btn"
              onClick={async () => {
                // Iteration 54 (Feb 27 2026) — Add to cart is now server-backed.
                // If the customer isn't signed in we redirect through /login with
                // a `next` param that returns them here — the CartProvider merges
                // their guest localStorage into the account cart on login.
                if (!time) { toast.error("Pick a date and time first"); return; }
                if (!user) {
                  // Push into guest localStorage first so the item isn't lost.
                  await addToCart({
                    service_id: s.id, service_title: s.title,
                    business_id: s.business_id, business_name: s.business_name,
                    price: Number(s.price || 0), deposit: depositAmount,
                    scheduled_at: time, notes: notes || "", image_url: s.image_url || "",
                  });
                  toast.info("Sign in to save your cart across devices");
                  nav(`/login?next=/services/${id}`);
                  return;
                }
                if (user.role !== "customer" && user.role !== "super_admin") {
                  toast.error("Only customers can add services to a cart");
                  return;
                }
                setAddingToCart(true);
                try {
                  await addToCart({
                    service_id: s.id, service_title: s.title,
                    business_id: s.business_id, business_name: s.business_name,
                    price: Number(s.price || 0), deposit: depositAmount,
                    scheduled_at: time, notes: notes || "", image_url: s.image_url || "",
                  });
                } finally {
                  setAddingToCart(false);
                }
              }}
              disabled={addingToCart}
              className="mt-2 w-full rounded-full font-medium py-3 border transition-colors disabled:opacity-50"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)", background: "transparent" }}
            >
              {addingToCart ? "Adding…" : "+ Add to cart"}
            </button>
            )}
            <p className="text-xs mt-3" style={{ color: "var(--gf-text-muted)" }} data-testid="book-helper-text">
              {requestMode
                ? <>Nothing is charged and no card is saved. <b style={{ color: "var(--gf-text)" }}>{s.business_name}</b> reviews your requested time and you finish payment only after they accept.</>
                : !q
                  ? <>Calculating the exact amounts for this appointment…</>
                  : q.total_today_cents > 0
                    ? <>You&rsquo;ll be securely redirected to our payment provider to pay <b style={{ color: "var(--gf-text)" }}>{money(q.total_today_cents)}</b> today. {q.timing_note} Cancel or update your card any time from My Bookings.</>
                    : <>{q.timing_note}</>
              }
            </p>
          </div>
        </div>
      </div>
      {reportOpen && (
        <ReportBusinessModal
          businessId={s.business_id}
          businessName={s.business_name}
          onClose={() => setReportOpen(false)}
          onSubmitted={() => setReportOpen(false)}
        />
      )}
      <Footer />
    </div>
  );
}
